import { useState, type FormEvent } from "react";
import { useSearchParams } from "react-router-dom";
import logoUrl from "@agile/brand/assets/logos/agile-mark.svg";
import { api, ApiError } from "../lib/api";
import { useAuth } from "../auth/AuthContext";
import { useDocumentTitle } from "../lib/useDocumentTitle";

const MIN = 10;

// Landing page for the emailed reset / set-password link (/reset?token=…).
// On success the API sets a session cookie, so we refresh auth and let App swap
// to the portal.
export default function ResetPassword() {
  useDocumentTitle("Set your password");
  const [params] = useSearchParams();
  const token = params.get("token") ?? "";
  const { refresh } = useAuth();
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  async function submit(e: FormEvent) {
    e.preventDefault();
    if (password.length < MIN) {
      setError(`Password must be at least ${MIN} characters.`);
      return;
    }
    if (password !== confirm) {
      setError("Those passwords don't match.");
      return;
    }
    setBusy(true);
    setError("");
    try {
      await api.post("/auth/reset", { token, password });
      // Clear the token from the URL, then load the now-authenticated session.
      window.history.replaceState(null, "", "/");
      await refresh();
    } catch (err) {
      setError(
        err instanceof ApiError
          ? err.message
          : "Something went wrong. Please try again.",
      );
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="login">
      <div className="login__card">
        <img className="login__logo" src={logoUrl} alt="Agile Software" />
        <h1 style={{ fontSize: 24 }}>Set your password</h1>
        <p className="muted" style={{ marginTop: 4 }}>
          Agile Software project portal
        </p>

        {!token ? (
          <p className="notice notice--error" style={{ marginTop: 16 }}>
            This link is invalid or has expired. Request a new one from the
            sign-in page.
          </p>
        ) : (
          <form
            onSubmit={submit}
            className="field"
            style={{ marginTop: 20, textAlign: "left" }}
          >
            {error && (
              <p className="notice notice--error" style={{ marginBottom: 8 }}>
                {error}
              </p>
            )}
            <label className="label" htmlFor="password">
              New password
            </label>
            <input
              id="password"
              className="input"
              type="password"
              required
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder={`At least ${MIN} characters`}
              autoComplete="new-password"
            />
            <label className="label" htmlFor="confirm" style={{ marginTop: 12 }}>
              Confirm password
            </label>
            <input
              id="confirm"
              className="input"
              type="password"
              required
              value={confirm}
              onChange={(e) => setConfirm(e.target.value)}
              placeholder="Re-enter your password"
              autoComplete="new-password"
            />
            <button
              type="submit"
              className="btn btn--primary btn--block"
              disabled={busy}
              style={{ marginTop: 12 }}
            >
              {busy ? "Saving…" : "Set password and sign in"}
            </button>
          </form>
        )}
      </div>
    </div>
  );
}
